import {
  hasUserPermissionsData,
  userHasAboveStandardAccess,
  userHasAdminAccess,
  userHasManagerOrAdminAccess,
} from 'shared/domain/user/hasGlobalRoleAccess';
import { UserWithPermissionsInDto } from 'shared/domain/user/types/inDto';
import { hasVisibleNoAccessRoleForProject } from 'shared/domain/permissions/projectPermissionsGuards';
import { NAVIGATION_ITEMS } from './constants';
import {
  NavigationDrawerBuildContext,
  NavigationItemDefinition,
  NavigationItemProps,
  NavigationPermissions,
} from './types';

function getNavigationPermissions(
  user: UserWithPermissionsInDto
): NavigationPermissions {
  return {
    inspections: userHasAboveStandardAccess(user),
    users: userHasManagerOrAdminAccess(user),
    projectSettings: userHasAdminAccess(user),
  };
}

/**
 * Base nav items stay visible (disabled while bootstrapping) so the sidebar
 * is never empty. Role-gated items appear only once permissions are known.
 */
function isNavigationItemVisible(
  item: NavigationItemDefinition,
  ctx: NavigationDrawerBuildContext
): boolean {
  if (item.canView) {
    return item.canView(ctx);
  }

  if (!item.access) {
    return true;
  }

  return ctx.isUserReady && ctx.permissions[item.access];
}

export function getNavigationDrawerState(
  user: UserWithPermissionsInDto,
  projectId: string
): {
  isUserReady: boolean;
  isNavigationDisabled: boolean;
  permissions: NavigationPermissions;
} {
  const isUserReady = hasUserPermissionsData(user);
  const hasNoAccessToProject = hasVisibleNoAccessRoleForProject(
    user.permissions,
    projectId
  );

  return {
    isUserReady,
    // PT-6852: disable drawer items until user permissions and projectId are
    // ready. Modules handle their own loading after navigation.
    isNavigationDisabled: !isUserReady || !projectId || hasNoAccessToProject,
    permissions: getNavigationPermissions(user),
  };
}

export function buildNavigationItems(
  ctx: NavigationDrawerBuildContext
): NavigationItemProps[] {
  return NAVIGATION_ITEMS.filter((item) =>
    isNavigationItemVisible(item, ctx)
  ).map((item) => ({
    visible: true,
    componentKey: item.key,
    textId: item.textId,
    Icon: item.icon,
    redirectAddress:
      typeof item.path === 'function' ? item.path(ctx) : item.path,
    isSubItem: item.subItem ?? false,
    disabled: ctx.isNavigationDisabled,
    navigationState: item.state?.(ctx),
    isNavigationExpanded: ctx.isNavigationExpanded,
    setCollapsed: ctx.setCollapsed,
  }));
}
